import React, { useEffect } from 'react';
import { ClerkProvider, SignedIn, SignedOut, RedirectToSignIn, useAuth } from '@clerk/clerk-react';
import { CLERK_TOKEN_TEMPLATE } from '../../shared/config';
import { setTokenGetter } from '../../shared/api/client';

// Bridges Clerk's session token into the API client seam. Mounted only inside <ClerkProvider>.
// The worker's clerkAuth expects a JWT from the `xlooop-workers` template.
function TokenBridge({ children }: { children: React.ReactNode }): React.ReactElement {
  const { getToken, isLoaded } = useAuth();
  useEffect(() => {
    setTokenGetter(() => getToken({ template: CLERK_TOKEN_TEMPLATE }));
  }, [getToken]);
  // Don't render the app until Clerk has resolved, so the first request carries a token.
  if (!isLoaded) return <></>;
  return <>{children}</>;
}

/**
 * With a Clerk key: real auth — signed-out users hit the Clerk sign-in, signed-in users get a
 * token-bridged app. Without a key (dev/MSW): render children directly; the client's token getter
 * stays returning null and fixtures ignore auth.
 */
export function AuthGate({
  clerkKey,
  children,
}: {
  clerkKey: string;
  children: React.ReactNode;
}): React.ReactElement {
  if (!clerkKey) return <>{children}</>;

  return (
    <ClerkProvider publishableKey={clerkKey}>
      <SignedIn>
        <TokenBridge>{children}</TokenBridge>
      </SignedIn>
      <SignedOut>
        <RedirectToSignIn />
      </SignedOut>
    </ClerkProvider>
  );
}
